import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  TextField,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowBack,
  ArrowForward,
  ArrowUpward,
  CloudUpload,
  Delete,
  Check,
  Clear,
  Refresh,
  Search,
} from '@mui/icons-material';
import * as subMatFolderActions from '../../store/subMatFolder';

const PDF_ACCEPT = '.pdf,application/pdf';

/** 용량·업로드 규칙이 정해지기 전까지 도구바에서만 숨긴다. 동작은 유지한다. */
const SHOW_ALL_UPLOAD = false;

/** 컬럼 헤더와 동일한 미색 */
const TOOLBAR_BG = 'rgba(0, 0, 0, 0.02)';

const buildBreadcrumb = (folder, folderMap) => {
  if (!folder) return [];
  const chain = [];
  let cur = folder;
  while (cur) {
    chain.unshift(cur);
    cur = cur.parentId ? folderMap[cur.parentId] : null;
  }
  return chain;
};

const PathSep = () => (
  <Typography
    component="span"
    variant="body2"
    color="text.secondary"
    sx={{ mx: 0.5, flexShrink: 0, userSelect: 'none' }}
  >
    ›
  </Typography>
);

/**
 * 윈도우 탐색기 주소창형 경로.
 * 전체 너비가 넘치면 앞쪽 계층을 … 로 접고(클릭 시 메뉴), 끝쪽 이름은 가능한 한 유지.
 */
const ExplorerPathBar = ({ crumbs, onNavigate }) => {
  const containerRef = useRef(null);
  const fullMeasureRef = useRef(null);
  const ellipsisMeasureRef = useRef(null);
  const segmentMeasureRefs = useRef([]);
  const [tailFrom, setTailFrom] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const crumbKey = useMemo(
    () => crumbs.map((c) => `${c.id}:${c.name}`).join('/'),
    [crumbs],
  );

  const fullPathTitle = useMemo(
    () => crumbs.map((c) => c.name).join(' › '),
    [crumbs],
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || crumbs.length === 0) return undefined;

    segmentMeasureRefs.current = segmentMeasureRefs.current.slice(0, crumbs.length);

    const recalc = () => {
      const available = container.clientWidth;
      const fullEl = fullMeasureRef.current;
      if (!fullEl) return;
      if (fullEl.scrollWidth <= available + 1) {
        setTailFrom(0);
        return;
      }

      const ellipsisW = ellipsisMeasureRef.current?.scrollWidth || 28;
      const segs = segmentMeasureRefs.current;
      let from = Math.max(0, crumbs.length - 1);
      for (let start = 0; start < crumbs.length; start += 1) {
        let width = start > 0 ? ellipsisW : 0;
        for (let i = start; i < crumbs.length; i += 1) {
          width += segs[i]?.scrollWidth || 0;
        }
        if (width <= available + 1) {
          from = start;
          break;
        }
      }
      setTailFrom(from);
    };

    recalc();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(recalc)
      : null;
    ro?.observe(container);
    window.addEventListener('resize', recalc);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', recalc);
    };
  }, [crumbKey, crumbs.length]);

  const collapsed = crumbs.slice(0, tailFrom);
  const visible = crumbs.slice(tailFrom);

  const renderCrumb = (b, isLast) => {
    if (isLast) {
      return (
        <Typography
          key={b.id}
          component="span"
          variant="body2"
          color="text.primary"
          noWrap
          sx={{
            fontWeight: 500,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {b.name}
        </Typography>
      );
    }
    return (
      <Link
        key={b.id}
        component="button"
        type="button"
        variant="body2"
        underline="hover"
        onClick={() => onNavigate?.(b.id)}
        noWrap
        sx={{
          border: 0,
          background: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          flexShrink: 0,
          p: 0,
          color: 'text.secondary',
        }}
      >
        {b.name}
      </Link>
    );
  };

  return (
    <Box
      ref={containerRef}
      title={fullPathTitle}
      sx={{
        position: 'relative',
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* 측정용 (비가시) */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          left: 0,
          top: 0,
        }}
      >
        <Box ref={fullMeasureRef} sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {crumbs.map((b, i) => (
            <Box
              key={`full-${b.id}`}
              ref={(el) => { segmentMeasureRefs.current[i] = el; }}
              sx={{ display: 'inline-flex', alignItems: 'center' }}
            >
              {i > 0 && <PathSep />}
              <Typography component="span" variant="body2" noWrap sx={{ fontWeight: i === crumbs.length - 1 ? 500 : 400 }}>
                {b.name}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box ref={ellipsisMeasureRef} sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <Typography
            component="span"
            variant="body2"
            sx={{ px: 0.5, fontWeight: 600, letterSpacing: 0.5 }}
          >
            …
          </Typography>
          <PathSep />
        </Box>
      </Box>

      {collapsed.length > 0 && (
        <>
          <Typography
            component="button"
            type="button"
            variant="body2"
            aria-label="상위 경로 더보기"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              border: 0,
              background: 'none',
              cursor: 'pointer',
              p: 0,
              px: 0.5,
              flexShrink: 0,
              fontWeight: 600,
              letterSpacing: 0.5,
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: 'action.hover', borderRadius: 0.5 },
            }}
          >
            …
          </Typography>
          <PathSep />
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            {collapsed.map((b) => (
              <MenuItem
                key={b.id}
                dense
                onClick={() => {
                  setMenuAnchor(null);
                  onNavigate?.(b.id);
                }}
              >
                {b.name}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {visible.map((b, i) => {
        const isLast = tailFrom + i === crumbs.length - 1;
        return (
          <Box
            key={b.id}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              minWidth: 0,
              ...(isLast ? { flex: '1 1 auto', overflow: 'hidden' } : { flexShrink: 0 }),
            }}
          >
            {i > 0 && <PathSep />}
            {renderCrumb(b, isLast)}
          </Box>
        );
      })}
    </Box>
  );
};

/**
 * 탐색기형 공통 크롬 — 액션 | 경로 | 검색 (한 줄)
 */
const SubMatFolderExplorerChrome = ({
  folder,
  folderMap,
  uploading = false,
  searchQuery = '',
  onSearchQueryChange,
  onNavigate,
  onCreateFolder,
  onDeleteFolder,
  hasDeletableSelection = false,
  onUploadFile,
  onRefresh,
  canGoBack = false,
  canGoForward = false,
  canGoUp = false,
  onGoBack,
  onGoForward,
  onGoUp,
  specOnly = false,
  onSpecOnlyChange,
  onDownloadAll,
  onUploadAllZip,
  onUploadAllFolder,
  activityLabel = '',
  busy = false,
  /** 모바일: 검색·파일 올리기·새로고침 */
  compact = false,
}) => {
  const uploadable = subMatFolderActions.isUploadableFolder(folder);
  const canAddChild = subMatFolderActions.canAddChildFolder(folder, folderMap);
  const canDelete = (folder && !folder.isSystem && !folder.linkView) || hasDeletableSelection;
  const breadcrumbs = buildBreadcrumb(folder, folderMap);
  const isSearching = Boolean(searchQuery.trim());
  const [allUploadAnchor, setAllUploadAnchor] = useState(null);

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) onUploadFile?.(file);
    e.target.value = '';
  };

  if (compact) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          bgcolor: TOOLBAR_BG,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.75,
            minWidth: 0,
          }}
        >
        <TextField
          size="small"
          placeholder="검색"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange?.(e.target.value)}
          disabled={!folder}
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiInputBase-root': {
              height: 32,
              bgcolor: 'background.paper',
            },
            '& .MuiInputBase-input': {
              py: 0,
              fontSize: '0.875rem',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="검색 지우기" onClick={() => onSearchQueryChange?.('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Tooltip title="파일 올리기">
          <span>
            <IconButton
              size="small"
              component="label"
              disabled={!folder || !uploadable || uploading}
              aria-label="파일 올리기"
            >
              <CloudUpload fontSize="small" />
              <input
                type="file"
                hidden
                accept={PDF_ACCEPT}
                disabled={!folder || !uploadable || uploading}
                onChange={handleInputChange}
              />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="새로고침">
          <span>
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={!onRefresh || busy}
              aria-label="새로고침"
            >
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        </Box>
        {uploading && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ px: 1, pb: 0.75 }}>
            {activityLabel || '업로드 중…'}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.75,
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0,
        flexWrap: { xs: 'wrap', md: 'nowrap' },
        bgcolor: TOOLBAR_BG,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
        <Tooltip title="이전">
          <span>
            <IconButton
              size="small"
              onClick={onGoBack}
              disabled={!canGoBack}
              aria-label="이전 폴더"
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="다음">
          <span>
            <IconButton
              size="small"
              onClick={onGoForward}
              disabled={!canGoForward}
              aria-label="다음 폴더"
            >
              <ArrowForward fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="위로">
          <span>
            <IconButton
              size="small"
              onClick={onGoUp}
              disabled={!canGoUp}
              aria-label="상위 폴더"
            >
              <ArrowUpward fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="새로고침">
          <span>
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={!onRefresh || busy}
              aria-label="폴더 목록 새로고침"
            >
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
        <Tooltip title="하위 폴더 만들기">
          <span>
            <IconButton
              size="small"
              onClick={onCreateFolder}
              disabled={!folder || !canAddChild}
              aria-label="하위 폴더"
            >
              <Add fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="PDF 업로드">
          <span>
            <IconButton
              size="small"
              component="label"
              disabled={!folder || !uploadable || uploading}
              aria-label="PDF 업로드"
            >
              <CloudUpload fontSize="small" />
              <input
                type="file"
                hidden
                accept={PDF_ACCEPT}
                disabled={!folder || !uploadable || uploading}
                onChange={handleInputChange}
              />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={hasDeletableSelection ? '선택 항목 삭제' : '폴더 삭제'}>
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={onDeleteFolder}
              disabled={!canDelete || busy}
              aria-label="폴더 삭제"
            >
              <Delete fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
        <Button
          size="small"
          disabled={uploading || !onDownloadAll}
          onClick={onDownloadAll}
          sx={{ height: 32, px: 1, textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          전체 다운로드
        </Button>
        {SHOW_ALL_UPLOAD && (
          <>
            <Button
              size="small"
              disabled={uploading || !onUploadAllZip}
              onClick={(e) => setAllUploadAnchor(e.currentTarget)}
              sx={{ height: 32, px: 1, textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              전체 업로드
            </Button>
            <Menu
              anchorEl={allUploadAnchor}
              open={Boolean(allUploadAnchor)}
              onClose={() => setAllUploadAnchor(null)}
            >
              <MenuItem onClick={() => { setAllUploadAnchor(null); onUploadAllZip?.(); }}>
                zip으로 업로드
              </MenuItem>
              <MenuItem onClick={() => { setAllUploadAnchor(null); onUploadAllFolder?.(); }}>
                폴더 업로드
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          borderRadius: 1,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.23)',
          overflow: 'hidden',
          '&:hover': {
            borderColor: 'text.primary',
          },
        }}
      >
        {!folder ? (
          <Typography variant="body2" color="text.secondary" noWrap>폴더를 선택하세요.</Typography>
        ) : isSearching ? (
          <Typography variant="body2" color="text.secondary" noWrap>
            「{searchQuery.trim()}」 검색 중
            <Typography
              component="button"
              variant="caption"
              color="primary"
              onClick={() => onSearchQueryChange?.('')}
              sx={{
                ml: 1,
                border: 0,
                background: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                p: 0,
                font: 'inherit',
              }}
            >
              검색 종료
            </Typography>
          </Typography>
        ) : (
          <ExplorerPathBar crumbs={breadcrumbs} onNavigate={onNavigate} />
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: { xs: 'auto', md: 0 } }}>
        <TextField
          size="small"
          placeholder="이 폴더에서 검색 (* ?)"
          value={searchQuery}
          onChange={(e) => onSearchQueryChange?.(e.target.value)}
          disabled={!folder}
          sx={{
            width: { xs: 160, sm: 260, md: 300 },
            '& .MuiInputBase-root': {
              height: 32,
              bgcolor: 'background.paper',
            },
            '& .MuiInputBase-input': {
              py: 0,
              fontSize: '0.875rem',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="검색 지우기" onClick={() => onSearchQueryChange?.('')}>
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {uploading && (
          <Typography variant="caption" color="text.secondary">{activityLabel || '업로드 중…'}</Typography>
        )}
        {onSpecOnlyChange && (
          <ToggleButton
            value="spec"
            size="small"
            selected={specOnly}
            onChange={() => onSpecOnlyChange(!specOnly)}
            aria-label="속성 폴더만"
            sx={{
              height: 32,
              px: 1.25,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              },
              gap: 0.5,
            }}
          >
            <Check sx={{ fontSize: 18 }} />
            속성 폴더만
          </ToggleButton>
        )}
      </Box>
    </Box>
  );
};

export default SubMatFolderExplorerChrome;
